import * as Astronomy from 'astronomy-engine';
import { normLon, toRad, type GeoPoint } from './geo';

export interface AstroBody {
  body: Astronomy.Body;
  name: string;
  abbr: string;
  color: string;
  /** Marker diameter in px */
  size: number;
}

export const ASTRO_BODIES: AstroBody[] = [
  { body: Astronomy.Body.Sun, name: 'Sun', abbr: 'Sun', color: '#fbbf24', size: 14 },
  { body: Astronomy.Body.Moon, name: 'Moon', abbr: 'Moon', color: '#cbd5e1', size: 12 },
  { body: Astronomy.Body.Mercury, name: 'Mercury', abbr: 'Me', color: '#d6b18b', size: 8 },
  { body: Astronomy.Body.Venus, name: 'Venus', abbr: 'V', color: '#fde68a', size: 8 },
  { body: Astronomy.Body.Mars, name: 'Mars', abbr: 'Ma', color: '#f87171', size: 8 },
  { body: Astronomy.Body.Jupiter, name: 'Jupiter', abbr: 'J', color: '#f0abfc', size: 8 },
  { body: Astronomy.Body.Saturn, name: 'Saturn', abbr: 'S', color: '#e7d7a9', size: 8 },
  { body: Astronomy.Body.Uranus, name: 'Uranus', abbr: 'U', color: '#67e8f9', size: 8 },
  { body: Astronomy.Body.Neptune, name: 'Neptune', abbr: 'N', color: '#93c5fd', size: 8 },
];

export interface BodyState extends AstroBody {
  /** Zenith point on Earth (where the body is directly overhead). */
  sub: GeoPoint;
  /** Altitude above the horizon at the observer location (degrees). */
  alt: number;
  /** Azimuth at the observer location (degrees, 0 = N, 90 = E). */
  az: number;
  rise?: Date | null;
  set?: Date | null;
  moonIllum?: number;
  moonPhaseName?: string;
}

/** Point on Earth where the body is at the zenith right now. */
export function subPoint(body: Astronomy.Body, date: Date): GeoPoint {
  const observer = new Astronomy.Observer(0, 0, 0);
  const equ = Astronomy.Equator(body, date, observer, true, true);
  const gst = Astronomy.SiderealTime(date); // hours
  return { lat: equ.dec, lon: normLon(15 * (equ.ra - gst)) };
}

/** Altitude/azimuth of a body as seen from a location. */
export function altAz(body: Astronomy.Body, date: Date, loc: GeoPoint): { alt: number; az: number } {
  const observer = new Astronomy.Observer(loc.lat, loc.lon, 0);
  const equ = Astronomy.Equator(body, date, observer, true, true);
  const hor = Astronomy.Horizon(date, observer, equ.ra, equ.dec, 'normal');
  return { alt: hor.altitude, az: hor.azimuth };
}

export function moonIllumination(date: Date): number {
  const phaseAngle = Astronomy.MoonPhase(date); // 0..360
  return ((1 - Math.cos(toRad(phaseAngle))) / 2) * 100;
}

const MOON_PHASES = [
  'New Moon',
  'Waxing Crescent',
  'First Quarter',
  'Waxing Gibbous',
  'Full Moon',
  'Waning Gibbous',
  'Last Quarter',
  'Waning Crescent',
];

export function moonPhaseName(date: Date): string {
  const phaseAngle = Astronomy.MoonPhase(date);
  const idx = Math.floor(((phaseAngle + 22.5) % 360) / 45);
  return MOON_PHASES[idx];
}

export function nextRiseSet(
  body: Astronomy.Body,
  date: Date,
  loc: GeoPoint,
): { rise: Date | null; set: Date | null } {
  const observer = new Astronomy.Observer(loc.lat, loc.lon, 0);
  const rise = Astronomy.SearchRiseSet(body, observer, +1, date, 1);
  const set = Astronomy.SearchRiseSet(body, observer, -1, date, 1);
  return { rise: rise?.date ?? null, set: set?.date ?? null };
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Web Mercator latitude limit used by Leaflet (EPSG:3857). */
export const MERCATOR_MAX_LAT = 85.05112878;

/** Overlay bounds matching the Mercator-projected night image. */
export const NIGHT_OVERLAY_BOUNDS: [[number, number], [number, number]] = [
  [-MERCATOR_MAX_LAT, -180],
  [MERCATOR_MAX_LAT, 180],
];

const MAX_MERC_Y = Math.log(Math.tan(Math.PI / 4 + toRad(MERCATOR_MAX_LAT) / 2));

/**
 * Renders the night side of Earth as a Web-Mercator-projected RGBA image.
 * Rows are linear in Mercator Y (not latitude), so Leaflet can stretch the
 * image over EPSG:3857 tiles without distortion. Includes a soft twilight
 * gradient (Sun 0° to -10°). `strength` scales the opacity (use a lower
 * value on light map tiles).
 */
export function renderNightOverlay(date: Date, strength = 1, width = 480, height = 240): string {
  const sun = subPoint(Astronomy.Body.Sun, date);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const img = ctx.createImageData(width, height);
  const sinSun = Math.sin(toRad(sun.lat));
  const cosSun = Math.cos(toRad(sun.lat));
  const twilight = Math.sin(toRad(10)); // widened twilight band for a softer look

  for (let y = 0; y < height; y++) {
    // Mercator Y for this row, then back-project to latitude.
    const my = MAX_MERC_Y * (1 - (2 * (y + 0.5)) / height);
    const lat = (2 * Math.atan(Math.exp(my)) - Math.PI / 2) * (180 / Math.PI);
    const sinLat = Math.sin(toRad(lat));
    const cosLat = Math.cos(toRad(lat));
    for (let x = 0; x < width; x++) {
      const lon = ((x + 0.5) / width) * 360 - 180;
      const cosZenith = sinSun * sinLat + cosSun * cosLat * Math.cos(toRad(lon - sun.lon));
      let alpha = 0;
      if (cosZenith < 0) {
        const t = Math.min(1, -cosZenith / twilight);
        alpha = (0.12 + 0.33 * smoothstep(t)) * strength;
      }
      const i = (y * width + x) * 4;
      img.data[i] = 9;
      img.data[i + 1] = 14;
      img.data[i + 2] = 38;
      img.data[i + 3] = Math.round(alpha * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL('image/png');
}
